import React from 'react'

import {
  RemoteDataState,
  SpinnerContainer,
  TechnoSpinner,
} from '@influxdata/clockface'

// broke-ass ttl: will show a techno spinner and try to fetch experiment info for about ~500ms
const MAX_FETCH_ATTEMPTS = 5

export type OptimizeId = string | number | null

interface Props {
  children: any
  id: OptimizeId
}

interface State {
  variant: OptimizeId
}

/**********************************************************************************
 *
 *  Usage of this component
 *
 *  Wrap your entire flag/experiment in <Experiment id={optimize_experiment_id}>
 *  Wrap your control variante in <Control id={optimize_control_id}>
 *  Wrap your experiment variants in <Variant id={optimize_variant_1_id}>
 *  Supply ids from google optimize as `id` prop to Experiment, Control, and Variant
 *
 *  <Experiment id="as023masgliwqe">
 *  <Control id="0">
 *    <a href="/checkout">Upgrade</a>
 *  </Control>
 *  <Variant id="1">
 *    <a href="/checkout">Go Unlimited Now</a>
 *  </Variant>
 *  <Variant id="2">
 *    <a href="/checkout">FREE IPHONES 🔥🔥</a>
 *  </Variant>
 *  </Experiment>
 *
/**********************************************************************************/

export class Experiment extends React.Component<Props, State> {
  protected fetchAttempts: number

  constructor(props: Props) {
    super(props)

    this.fetchAttempts = 0

    this.state = {
      variant: null,
    }
  }

  get loadingState(): RemoteDataState {
    if (this.state.variant === null) {
      return RemoteDataState.Loading
    }
    return RemoteDataState.Done
  }

  // Try to get the variant from Google's Optimize A/B service.
  // If we can't find the variant, try again in about ~100ms.
  // If we can't find it after the maximum attempts, show the default,
  // and don't try to fetch again.
  fetchAndSetVariant = (): void => {
    if (this.fetchAttempts >= MAX_FETCH_ATTEMPTS) {
      this.setState({variant: 0})
      return
    }

    this.fetchAttempts++
    if (window.google_optimize) {
      const variant = window.google_optimize.get(this.props.id)
      this.setState({variant})
      return
    }

    setTimeout(this.fetchAndSetVariant, 100)
  }

  componentDidMount() {
    // Google Optimize will fire and bucket when the event 'optimize.activate' is fired.
    // If you're using this wrapper with your components, be sure to change the Activation Event
    // in Google Optimize from `page load` to `custom event: 'optimize.activate'`
    window.dataLayer.push({event: 'optimize.activate'})
    this.fetchAndSetVariant()
  }

  render() {
    // Use the first child as a default. Convering children to a flattened array makes
    // them much easier to work with.
    const children = React.Children.toArray(
      this.props.children
    ) as JSX.Element[]

    // Try to find the <Control> element to use as a default. Failing that, fall back to the first child
    let variantToShow =
      children.find((child) => child.type.name === 'Control') || children[0]
    children.forEach((child) => {
      if (
        child.props &&
        child.props.id &&
        child.props.id == this.state.variant
      ) {
        variantToShow = child
        return
      }
    })

    if (!variantToShow) {
      return null
    }

    return (
      <SpinnerContainer
        loading={this.loadingState}
        spinnerComponent={<TechnoSpinner />}
      >
        {variantToShow}
      </SpinnerContainer>
    )
  }
}

interface VariantProps {
  children: any
  id: OptimizeId
}

export const Variant = (props: VariantProps): JSX.Element => {
  return props.children
}

export const Control = (props: VariantProps): JSX.Element => {
  return props.children
}
